<template>
  <div class="row mb-3" v-if="isScopedSearch && !withFilters">
    <div class="col-12">
      <search-suggestion-button
        :scope="scope"
        @clicked="setKeyword"
      />
    </div>
  </div>
  <div class="row mb-3" v-else-if="(isScopedSearch && !groupedFilters.hasActiveFilters) || groupedFilters.hasActiveFilters">
    <div class="col-12">
      <template v-if="isScopedSearch && !groupedFilters.hasActiveFilters">
        <search-suggestion-button
          :scope="scope"
          @clicked="setKeyword"
        />
      </template>
      <template v-if="groupedFilters.hasActiveFilters">
        <a href="#" @click.prevent="$emit('resetFilters')" class="t-sm">
          Clear all filters
        </a>
      </template>
    </div>

    <!-- If there are any active filters, simply show these -->
    <div class="col-12 search_selected-filters mt-1 d-flex flex-wrap" v-if="groupedFilters.hasActiveFilters">
      <dropdown
        v-for="group of groupedFilters.groups"
        :key="group.key"
        :closeOnClick="false"
        :class="'dd-wide pr-1 pb-0'"
        :trigger="'click'"
      >
        <template #button>
          <span class="list-item-search t-600 t-sm">
            <span class="mr-1"
              v-tooltip="{ content: 'View all filters', offset: 5 }"
            >{{ group.parent.label }}
              <span class="filter-count mx-1">{{ group.buckets.length }}</span>
            </span>
            <i
              @click.prevent="deactivateGroup(group)"
              class="fa fa-close t-xs"
              v-tooltip="{ content: 'Remove these filters', offset: 8 }"
            />
          </span>
        </template>
        <div class="d-flex flex-wrap">
          <span
            v-for="(bucket, j) in group.buckets"
            :key="`group-bucket-${group.key}-${j}`"
            @click.prevent="$emit('deactivateFilter', bucket, bucket.filterName, filters)"
            class="mr-1 list-item-search single-filter t-overflow t-sm"
            title="Remove this filter"
          >
            <span v-if="bucket.type === 'date'" class="t-600 t-sm">{{ bucket.filterLabel }}:&nbsp;</span>{{ getValue(bucket) | humanize }}
            <i class="fa fa-close t-xs" />
          </span>
        </div>
      </dropdown>

      <span
        v-for="(bucket, i) in groupedFilters.buckets"
        :key="i"
        class="list-item-search single-filter mr-1 t-sm"
        @click.prevent="deactivateBucket(bucket)" v-tooltip="bucket.filterLabel ? `${bucket.filterLabel}` : ''"
      >
        <span v-if="bucket.type === 'date'" class="t-600 t-sm">{{ bucket.filterLabel }}:&nbsp;</span>{{ getValue(bucket) | humanize }}
        <i class="fa fa-close t-xs" />
      </span>
    </div>
  </div>
</template>

<script>
import Dropdown from '@/components/shared/form/Dropdown'
import SearchSuggestionButton from '@/components/search/SearchSuggestionButton'

export default {
  name: 'selectedFilter',
  components: {
    Dropdown,
    SearchSuggestionButton
  },
  props: {
    filters: {
      type: Object,
      required: true
    },
    isScopedSearch: {
      type: Boolean,
      default: false
    },
    scope: {
      type: String,
      default: ''
    },
    withFilters: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  computed: {
    groupedFilters () {
      const buckets = []
      const groupBuckets = {}
      let hasActiveFilters = false

      // We rely on the route solely to see if we should show the "Exact match"/"Non Bookmarked" bucket.
      // Because it might happen that we don't have the filterSet when there are no results for it while the filter is still active
      const bucketConfigs = [
        {
          key: 'exact_match',
          label: 'Only show exact query matches'
        },
        {
          key: 'exclude_bookmarked',
          label: 'Exclude bookmarked results'
        }
      ]

      if (this.$route.query && this.$route.query.qf) {
        bucketConfigs.forEach(({ key, label }) => {
          if (this.$route.query.qf.includes(key)) {
            buckets.push({
              filterName: key,
              value: key,
              label: label,
              active: true
            })
            hasActiveFilters = true
          }
        })
      }

      Object.entries(this.filters).forEach(([key, value]) => {
        if (value.buckets && !value.hide) {
          value.buckets.forEach(bucket => {
            if (bucket.active) {
              hasActiveFilters = true
              bucket.filterName = key
              bucket.type = value.type
              bucket.filterLabel = value.label

              if (value.format) bucket.format = value.format

              if (!groupBuckets[key]) {
                groupBuckets[key] = {
                  parent: {
                    value: key,
                    label: value.label
                  },
                  key: key,
                  buckets: [bucket]
                }
              } else {
                groupBuckets[key].buckets.push(bucket)
              }
            }
          })
        }
      })

      // Double check if we have no set orphans/parents that have to be transferred/killed off
      const groupsToDelete = []
      Object.values(groupBuckets).forEach(group => {
        if (!group.parent) {
          buckets.push(...group.buckets)
          groupsToDelete.push(group.key)
        } else if (group.buckets && group.buckets.length < 2) {
          buckets.unshift(...group.buckets)
          groupsToDelete.push(group.key)
        }
      })
      groupsToDelete.forEach(g => {
        delete groupBuckets[g]
      })

      return {
        buckets,
        groups: groupBuckets,
        hasActiveFilters
      }
    }
  },
  methods: {
    deactivateBucket (bucket) {
      if (bucket.filterName === 'exact_match' || bucket.filterName === 'exclude_bookmarked') {
        bucket.active = false
        this.$emit('setToggled', bucket)
      } else {
        this.$emit('deactivateFilter', bucket, bucket.filterName, this.filters)
      }
    },
    deactivateGroup (group) {
      // Figure out how to deactivate all buckets of a certain category
      group.buckets.forEach(bucket => {
        bucket.active = false
      })
      setTimeout(() => {
        this.$emit('deactivateFilter', group.buckets[0], group.buckets[0].filterName, this.filters)
      }, 0)
    },
    getValue (bucket) {
      if (bucket.type === 'date') {
        return this.$options.filters.formatDate(bucket.value, bucket.format.toUpperCase())
      }
      return bucket.label ? bucket.label : this.$options.filters.humanize(bucket.value)
    },
    setKeyword (keyword) {
      this.$emit('setKeyword', keyword)
    }
  }
}
</script>
